import { useState, useMemo, useCallback } from 'react';
import { Shift } from '@sdk/automatedBreaks/types/Shift';
import getBreaksSummary from '@sdk/automatedBreaks/helpers/getBreaksSummary';
import { canEndAutomatedBreak } from '@sdk/automatedBreaks/utils';
import getCurrentBreak from '@sdk/automatedBreaks/helpers/getCurrentBreak';
import getBreaksToAttest from '@sdk/automatedBreaks/helpers/getBreaksToAttest';

import canTakeBreak from '@kiosk/automatedBreaks/helpers/canTakeAutomatedBreak';
import { AutomatedBreakViewType } from '@kiosk/common/components/UserPanel/ClockedInViewWithAutomatedBreaks/clockedInViewWithAutomatedBreaks.types';
import { useAppSelector } from '@kiosk/common/hooks/redux';
import { useAutomatedBreakSettings } from '@kiosk/common/hooks/useAutomatedBreakSettings';

type Props = { shift: Shift; startAutomatedBreak: VoidFunction };

export const useAutomatedBreaks = ({ shift, startAutomatedBreak }: Props) => {
  const [automatedBreakScreen, setAutomatedBreakScreen] = useState<AutomatedBreakViewType>(
    'clockedIn'
  );
  const automatedBreaksSettings = useAutomatedBreakSettings();
  const { orgSettings } = useAppSelector(state => state.timeClock.data);

  const canTakeAutomatedBreak = useMemo(() => {
    const { mealBreaksTaken, restBreaksTaken } = getBreaksSummary(
      shift.timesheetEntries,
      shift.dtstart,
      shift.dtend
    );

    return canTakeBreak({
      shift,
      automatedBreaksSettings,
      mealBreaksTaken,
      restBreaksTaken
    });
  }, [automatedBreaksSettings, shift]);

  const missedBreaks = useMemo(() => getBreaksToAttest(orgSettings, shift), [orgSettings, shift]);

  const canFinishAutomatedBreak = useCallback(() => {
    const { breakRecords } = getBreaksSummary(shift.timesheetEntries, shift.dtstart, shift.dtend);
    const currentBreak = getCurrentBreak(breakRecords);

    return currentBreak ? canEndAutomatedBreak(currentBreak) : false;
  }, [shift.dtend, shift.dtstart, shift.timesheetEntries]);

  return {
    canTakeMealBreak: canTakeAutomatedBreak.meal,
    canTakeRestBreak: canTakeAutomatedBreak.rest,
    startAutomatedBreak,
    automatedBreakScreen,
    setAutomatedBreakScreen,
    canFinishAutomatedBreak,
    missedBreaks,
    automatedBreaksSettings,
    orgSettings
  };
};
